/* Versa Posh sectional builder
 Spec: docs/rebuild/BUILDER_SPEC.md

 A layout is a run of pieces that may turn up to two corners (sofa, L, U),
 plus ottomans attached in front of seats. The run is drawn in plan view
 as SVG and every piece is added to the cart individually, so a layout
 always costs exactly the sum of its pieces. */
(function () {
'use strict';

var UNIT = 100; // model units per nominal seat depth
var VB_W = 1200;
var VB_H = 900;
var NOMINAL = { seat: [1, 1], arm: [1.25, 1], corner: [1, 1], ottoman: [1, 1], console: [0.4, 1] };
var ROLE = { armL: 'arm', armR: 'arm', seat: 'seat', corner: 'corner', console: 'console' };
var TOKEN = { armL: 'aL', armR: 'aR', seat: 's', corner: 'c', console: 'k' };
var FROM_TOKEN = { aL: 'armL', aR: 'armR', s: 'seat', c: 'corner', k: 'console' };
// Headings in screen space. A clockwise turn is +1; the front of a piece
// faces HEAD[(h + 1) % 4], the right-hand side of travel.
var HEAD = [[1, 0], [0, 1], [-1, 0], [0, -1]];
var ANGLE = [0, 90, 180, 270];
var SEATING = { seat: 1, armL: 1, armR: 1 };

function $(root, sel) { return root.querySelector(sel); }
function $$(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
function esc(s) {
  return String(s).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}
// Normalise an option value ("Alloy Gray", "Rolled Arm", "Left") to a key.
function keyOf(v, keys) {
  v = String(v || '').toLowerCase();
  for (var i = 0; i < keys.length; i++) if (v.indexOf(keys[i]) > -1) return keys[i];
  return v;
}
function overlaps(a, b) {
  var t = 1; // shared edges are fine
  return a.x1 > b.x0 + t && b.x1 > a.x0 + t && a.y1 > b.y0 + t && b.y1 > a.y0 + t;
}
function bounds(list) {
  return list.reduce(function (a, q) {
    return { x0: Math.min(a.x0, q.x0), y0: Math.min(a.y0, q.y0), x1: Math.max(a.x1, q.x1), y1: Math.max(a.y1, q.y1) };
  }, { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity });
}
// sessionStorage can throw (private mode, blocked storage); it is only a convenience.
function store(key, value) {
  try { window.sessionStorage.setItem(key, value); } catch (e) { /* ignore */ }
}
function recall(key) {
  try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
}
function track(name, data) {
  if (window.vpTrack) window.vpTrack(name, data);
}
function nextFrame(fn) {
  window.requestAnimationFrame(function () { window.requestAnimationFrame(fn); });
}
var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function Builder(root) {
  this.root = root;
  this.cfg = JSON.parse($(root, '[data-vp-config]').textContent);
  this.svg = $(root, '[data-vp-svg]');
  this.world = $(root, '[data-vp-world]');
  this.fabricImage = $(root, '[data-vp-fabric-image]');
  this.fabricId = this.cfg.id + '-fabric';
  this.soldId = this.cfg.id + '-soldout';
  this.addBtn = $(root, '[data-vp-add]');
  this.stickyBtn = $(root, '[data-vp-sticky-add]');
  this.uid = 0;
  this.nodes = {};
  this.selection = null;
  this.busy = false;
  this.dirty = false;
  this.money = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.cfg.currency || 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 });
  this.labels = {};
  var pieces = this.cfg.pieces || {};
  var self = this;
  ['seat', 'arm', 'corner', 'console', 'ottoman'].forEach(function (r) {
    self.labels[r] = (pieces[r] && pieces[r].label) || r;
  });
  this.setupScale();
  this.presets = (this.cfg.presets || []).map(function (p) {
    return { label: p.label, run: self.parseRun(p.run), ottomans: p.ottomans || 0 };
  }).filter(function (p) { return p.run && self.validRun(p.run); });

  this.state = { color: 'tofu', arm: 'roll', run: [], ottomans: {} };
  this.restore();
  this.bind();
  this.render(true);
  this.root.classList.add('is-ready');
}

var P = Builder.prototype;

// Scale
P.setupScale = function () {
  var pieces = this.cfg.pieces || {};
  var roles = ['seat', 'arm', 'corner', 'ottoman', 'console'];
  var inches = roles.every(function (r) {
    return !pieces[r] || (pieces[r].widthIn > 0 && pieces[r].depthIn > 0);
  }) && pieces.seat && pieces.seat.depthIn > 0;
  this.inches = !!inches;
  this.pxPerInch = inches ? UNIT / pieces.seat.depthIn : null;
};
P.size = function (role) {
  var p = this.cfg.pieces[role];
  if (this.inches && p) return [p.widthIn * this.pxPerInch, p.depthIn * this.pxPerInch];
  var n = NOMINAL[role];
  return [n[0] * UNIT, n[1] * UNIT];
};

// Model
P.piece = function (type) {
  this.uid += 1;
  return { id: 'p' + this.uid, type: type };
};
P.parseRun = function (text) {
  var self = this;
  var types = String(text || '').split(',').map(function (t) { return t.trim(); }).filter(Boolean);
  if (!types.length) return null;
  for (var i = 0; i < types.length; i++) if (!ROLE[types[i]]) return null;
  return types.map(function (t) { return self.piece(t); });
};
P.validRun = function (run) {
  var n = run.length;
  if (!n || n > (this.cfg.maxPieces || 12)) return false;
  var corners = 0;
  var seats = 0;
  for (var i = 0; i < n; i++) {
    var t = run[i].type;
    if (t === 'armL' && i !== 0) return false;
    if (t === 'armR' && i !== n - 1) return false;
    if ((i === 0 || i === n - 1) && (t === 'console' || t === 'corner')) return false;
    if (t === 'corner') {
      corners += 1;
      if (i > 0 && run[i - 1].type === 'corner') return false;
    }
    if (t !== 'console') seats += 1;
  }
  return corners <= 2 && seats >= 1;
};

// Walk the run and place every piece. Returns world rectangles.
P.geometry = function (run, ottomans) {
  var corners = run.filter(function (p) { return p.type === 'corner'; }).length;
  var h = corners ? 3 : 0; // start upward when the run turns, so it reads as an L or U
  var x = 0;
  var y = 0;
  var items = [];
  var byId = {};
  var self = this;
  function toWorld(p, lx, ly) {
    var H = HEAD[p.h];
    var R = HEAD[(p.h + 1) % 4];
    return [p.x + H[0] * lx + R[0] * ly, p.y + H[1] * lx + R[1] * ly];
  }
  function box(p, lx, ly, w, d) {
    var a = toWorld(p, lx, ly);
    var b = toWorld(p, lx + w, ly + d);
    return { x0: Math.min(a[0], b[0]), y0: Math.min(a[1], b[1]), x1: Math.max(a[0], b[0]), y1: Math.max(a[1], b[1]) };
  }
  run.forEach(function (piece, i) {
    var sz = self.size(ROLE[piece.type]);
    var it = { id: piece.id, type: piece.type, index: i, x: x, y: y, h: h, w: sz[0], d: sz[1] };
    it.rect = box(it, 0, 0, it.w, it.d);
    it.toWorld = function (lx, ly) { return toWorld(it, lx, ly); };
    items.push(it);
    byId[piece.id] = it;
    var H = HEAD[h];
    if (piece.type === 'corner') {
      var R = HEAD[(h + 1) % 4];
      x += H[0] * it.w + R[0] * it.d;
      y += H[1] * it.w + R[1] * it.d;
      h = (h + 1) % 4;
    } else {
      x += H[0] * it.w;
      y += H[1] * it.w;
    }
  });
  var osz = this.size('ottoman');
  var otto = [];
  Object.keys(ottomans || {}).forEach(function (pid) {
    var host = byId[pid];
    if (!host) return;
    var lx = (host.w - osz[0]) / 2;
    var ly = host.d; // flush, as a chaise sits
    var o = { id: 'o' + pid, host: pid, type: 'ottoman', h: host.h, w: osz[0], d: osz[1] };
    var at = host.toWorld(lx, ly);
    o.x = at[0];
    o.y = at[1];
    o.rect = box(host, lx, ly, osz[0], osz[1]);
    otto.push(o);
  });
  return { items: items, byId: byId, ottomans: otto };
};

// Keep ottomans that still fit after the run changes.
P.settle = function (run, ottomans) {
  var ids = {};
  run.forEach(function (p) { if (SEATING[p.type]) ids[p.id] = true; });
  var kept = {};
  var self = this;
  var count = 0;
  run.forEach(function (p) {
    if (!ottomans[p.id] || !ids[p.id] || count >= (self.cfg.maxOttomans || 0)) return;
    var trial = Object.assign({}, kept);
    trial[p.id] = true;
    if (self.ottomansFit(run, trial)) {
      kept = trial;
      count += 1;
    }
  });
  return kept;
};
P.ottomansFit = function (run, ottomans) {
  var g = this.geometry(run, ottomans);
  for (var i = 0; i < g.ottomans.length; i++) {
    var o = g.ottomans[i];
    for (var j = 0; j < g.items.length; j++) if (overlaps(o.rect, g.items[j].rect)) return false;
    for (var k = i + 1; k < g.ottomans.length; k++) if (overlaps(o.rect, g.ottomans[k].rect)) return false;
  }
  return true;
};

// Pricing
P.variantFor = function (role, facing) {
  var piece = this.cfg.pieces[role];
  if (!piece || !piece.product) return null;
  var opts = piece.product.options.map(function (o) { return String(o).toLowerCase(); });
  var ci = -1;
  var ai = -1;
  var fi = -1;
  opts.forEach(function (o, i) {
    if (o.indexOf('colo') === 0) ci = i;
    else if (o.indexOf('arm') > -1) ai = i;
    else if (/facing|side|hand/.test(o)) fi = i;
  });
  var s = this.state;
  var match = piece.product.variants.filter(function (v) {
    return (ci < 0 || keyOf(v.options[ci], ['tofu', 'alloy', 'fawn']) === s.color) &&
      (ai < 0 || keyOf(v.options[ai], ['track', 'roll']) === s.arm) &&
      (fi < 0 || !facing || keyOf(v.options[fi], ['left', 'right']) === facing);
  });
  var v = match[0] || null;
  if (v) v.hasFacing = fi > -1;
  return v;
};
P.lines = function () {
  var self = this;
  var lines = [];
  var byKey = {};
  function add(role, facing) {
    var v = self.variantFor(role, facing);
    var key = v ? 'v' + v.id : role + (facing || '');
    var line = byKey[key];
    if (!line) {
      var label = self.labels[role];
      if (v && v.hasFacing && facing) label += facing === 'left' ? ' (left)' : ' (right)';
      line = byKey[key] = { role: role, label: label, variant: v, qty: 0, facing: v && v.hasFacing ? facing : null };
      lines.push(line);
    }
    line.qty += 1;
  }
  this.state.run.forEach(function (p) {
    add(ROLE[p.type], p.type === 'armL' ? 'left' : p.type === 'armR' ? 'right' : null);
  });
  Object.keys(this.state.ottomans).forEach(function () { add('ottoman', null); });
  lines.forEach(function (l) {
    var v = l.variant;
    l.price = v ? v.price * l.qty : 0;
    l.ok = !!(v && v.available && (!v.tracked || v.qty == null || v.qty >= l.qty));
    l.left = v && v.qty != null && v.qty > 0 && v.qty <= (self.cfg.lowStock || 0) ? v.qty : null;
  });
  return lines;
};
P.colorLabel = function () {
  var c = this.state.color;
  var hit = (this.cfg.colors || []).filter(function (x) { return x.key === c; })[0];
  return hit ? hit.label : c;
};
P.armLabel = function () {
  return this.state.arm === 'track' ? 'Track arm' : 'Roll arm';
};
P.unavailableText = function (line) {
  var where = line.role === 'arm' ? this.colorLabel() + ' with a ' + this.armLabel().toLowerCase() : this.colorLabel();
  if (!line.variant) return line.label + ' isn\u2019t available in ' + where + ' right now.';
  if (line.variant.available && line.variant.qty != null) {
    return 'Only ' + line.variant.qty + ' ' + line.label.toLowerCase() + ' left in ' + where + '.';
  }
  return line.label + ' isn\u2019t available in ' + where + ' right now.';
};

// State io
P.encode = function () {
  var s = this.state;
  var idx = [];
  s.run.forEach(function (p, i) { if (s.ottomans[p.id]) idx.push(i); });
  return s.run.map(function (p) { return TOKEN[p.type]; }).join('.') + '~o' + idx.join('.') + '~' + s.color + '~' + s.arm;
};
P.decode = function (str) {
  var parts = String(str || '').split('~');
  var tokens = (parts[0] || '').split('.');
  var self = this;
  var run = [];
  for (var i = 0; i < tokens.length; i++) {
    if (!FROM_TOKEN[tokens[i]]) return null;
    run.push(this.piece(FROM_TOKEN[tokens[i]]));
  }
  if (!this.validRun(run)) return null;
  var ott = {};
  String((parts[1] || 'o').slice(1)).split('.').filter(Boolean).forEach(function (n) {
    var p = run[parseInt(n, 10)];
    if (p && SEATING[p.type]) ott[p.id] = true;
  });
  var colors = (this.cfg.colors || []).map(function (c) { return c.key; });
  return {
    run: run,
    ottomans: self.settle(run, ott),
    color: colors.indexOf(parts[2]) > -1 ? parts[2] : 'tofu',
    arm: parts[3] === 'track' ? 'track' : 'roll'
  };
};
P.fromPreset = function (preset) {
  var self = this;
  var run = preset.run.map(function (p) { return self.piece(p.type); });
  var ott = {};
  var left = preset.ottomans || 0;
  run.forEach(function (p) {
    if (left > 0 && SEATING[p.type]) { ott[p.id] = true; left -= 1; }
  });
  return { run: run, ottomans: this.settle(run, ott) };
};
P.findPreset = function (label) {
  label = String(label || '').toLowerCase();
  return this.presets.filter(function (p) { return p.label.toLowerCase() === label; })[0] || null;
};
P.restore = function () {
  var params = new URLSearchParams(window.location.search);
  var dealer = params.get('dealer');
  if (dealer) store('vp-dealer', dealer.slice(0, 64));
  var decoded = this.decode(params.get('layout')) || this.decode(recall('vp-sb:' + window.location.pathname));
  if (decoded) {
    this.state = decoded;
    return;
  }
  var start = this.cfg.startPreset;
  var preset = this.findPreset(start);
  if (!preset && start) {
    var run = this.parseRun(start);
    if (run && this.validRun(run)) preset = { run: run, ottomans: 0 };
  }
  preset = preset || this.findPreset(this.cfg.defaultPreset) || this.presets[0];
  if (!preset) preset = { run: [this.piece('armL'), this.piece('seat'), this.piece('armR')], ottomans: 0 };
  var st = this.fromPreset(preset);
  this.state.run = st.run;
  this.state.ottomans = st.ottomans;
};
P.persist = function () {
  if (!this.dirty) return;
  var code = this.encode();
  store('vp-sb:' + window.location.pathname, code);
  try {
    var url = new URL(window.location.href);
    url.searchParams.set('layout', code);
    window.history.replaceState(window.history.state, '', url.toString());
  } catch (e) { /* older browsers: the session copy is enough */ }
};
P.shareUrl = function () {
  var url = new URL(window.location.origin + window.location.pathname);
  url.searchParams.set('layout', this.encode());
  var dealer = recall('vp-dealer');
  if (dealer) url.searchParams.set('dealer', dealer);
  return url.toString();
};

// Edits
P.touch = function () {
  if (this.touched) return;
  this.touched = true;
  track('vp_builder_open', { layout: this.encode() });
};
P.commit = function (run, ottomans, select) {
  this.touch();
  this.state.run = run;
  this.state.ottomans = this.settle(run, ottomans || this.state.ottomans);
  this.selection = select || null;
  this.dirty = true;
  this.render(false);
};
P.insertOptions = function (slot) {
  var run = this.state.run;
  var self = this;
  var types = ['seat', 'corner', 'console'];
  if (slot.at === 0 && run[0] && run[0].type !== 'armL') types.push('armL');
  if (slot.at === run.length && run[run.length - 1] && run[run.length - 1].type !== 'armR') types.push('armR');
  return types.map(function (t) {
    var piece = self.piece(t);
    var next = run.slice(0, slot.at).concat([piece], run.slice(slot.at));
    return self.validRun(next) ? { type: t, run: next, piece: piece } : null;
  }).filter(Boolean);
};
P.slots = function () {
  var run = this.state.run;
  var n = run.length;
  var list = [
    { key: 'start', at: run[0] && run[0].type === 'armL' ? 1 : 0, end: 'start' },
    { key: 'end', at: run[n - 1] && run[n - 1].type === 'armR' ? n - 1 : n, end: 'end' }
  ];
  var sel = this.selection;
  var focusId = sel && sel.kind === 'piece' ? sel.id : sel && sel.kind === 'slot' && sel.from ? sel.from.id : null;
  if (focusId) {
    var i = this.indexOf(focusId);
    if (i > 0 && i !== list[0].at) list.push({ key: 'b' + i, at: i });
    if (i < n - 1 && i + 1 !== list[1].at) list.push({ key: 'b' + (i + 1), at: i + 1 });
  }
  var self = this;
  return list.filter(function (s) { return self.insertOptions(s).length; });
};
P.indexOf = function (id) {
  for (var i = 0; i < this.state.run.length; i++) if (this.state.run[i].id === id) return i;
  return -1;
};

// Render
P.render = function (instant) {
  var root = this.root;
  if (instant || reduceMotion) root.classList.add('is-instant');
  $$(root, '[data-vp-color]').forEach(function (r) { r.checked = r.value === this.state.color; }, this);
  $$(root, '[data-vp-arm]').forEach(function (r) { r.checked = r.value === this.state.arm; }, this);
  var swatch = $(root, '[data-vp-color]:checked');
  if (swatch && this.fabricImage.getAttribute('href') !== swatch.getAttribute('data-swatch')) {
    this.fabricImage.setAttribute('href', swatch.getAttribute('data-swatch'));
  }
  root.setAttribute('data-color', this.state.color);
  var name = $(root, '[data-vp-color-name]');
  if (name) name.textContent = this.colorLabel();

  this.lineItems = this.lines();
  this.geo = this.geometry(this.state.run, this.state.ottomans);
  this.drawCanvas();
  this.drawActions();
  this.drawSummary();
  this.drawPresets();
  this.drawArmNote();
  this.persist();
  if (instant || reduceMotion) {
    var self = this;
    nextFrame(function () { self.root.classList.remove('is-instant'); });
  }
};

// Scale the drawing to fill the canvas. The + buttons keep a fixed size on
// screen, so their footprint depends on the scale: settle it over a few passes.
P.fit = function (geo, slots) {
  var px = this.svg.clientWidth || VB_W;
  var self = this;
  this.slotPx = px < 500 ? 17 : 22;
  var pad = 28 * VB_W / px;
  var rects = geo.items.map(function (i) { return i.rect; }).concat(geo.ottomans.map(function (o) { return o.rect; }));
  var s = 1;
  var b;
  for (var pass = 0; pass < 3; pass++) {
    var r = (this.slotPx * 1.35 * VB_W / px) / s;
    var all = rects.concat(slots.map(function (sl) {
      var pt = self.slotPoint(sl, r / 1.35);
      return { x0: pt[0] - r, y0: pt[1] - r, x1: pt[0] + r, y1: pt[1] + r };
    }));
    b = bounds(all);
    s = Math.min((VB_W - pad * 2) / (b.x1 - b.x0), (VB_H - pad * 2) / (b.y1 - b.y0), 2.6);
  }
  return { s: s, tx: (VB_W - (b.x1 - b.x0) * s) / 2 - b.x0 * s, ty: (VB_H - (b.y1 - b.y0) * s) / 2 - b.y0 * s, box: b };
};

function rect(x, y, w, h, r, cls, fill) {
  return '<rect class="' + cls + '" x="' + x.toFixed(1) + '" y="' + y.toFixed(1) + '" width="' + Math.max(w, 0).toFixed(1) +
    '" height="' + Math.max(h, 0).toFixed(1) + '" rx="' + r + '"' + (fill ? ' fill="' + fill + '"' : '') + '></rect>';
}

// Plan-view drawing in local space: x runs along the run, y from the back (0) to the front (d).
P.shape = function (type, w, d, sold) {
  var fab = 'url(#' + this.fabricId + ')';
  var bd = d * 0.24;
  var inset = d * 0.05;
  var roll = this.state.arm === 'roll';
  var aw = roll ? d * 0.2 : d * 0.15;
  var ar = roll ? aw / 2 : 4;
  var out = [rect(-6, -6, w + 12, d + 12, 12, 'vp-sb__ring')];
  out.push(rect(0, 0, w, d, 8, 'vp-sb__base', fab));
  if (type === 'console') {
    out.push(rect(w * 0.16, d * 0.08, w * 0.68, d * 0.56, 4, 'vp-sb__line'));
    out.push('<circle class="vp-sb__cup" cx="' + (w / 2).toFixed(1) + '" cy="' + (d * 0.74).toFixed(1) + '" r="' + (w * 0.2).toFixed(1) + '"></circle>');
    out.push('<circle class="vp-sb__cup" cx="' + (w / 2).toFixed(1) + '" cy="' + (d * 0.89).toFixed(1) + '" r="' + (w * 0.2).toFixed(1) + '"></circle>');
  } else if (type === 'ottoman') {
    out.push(rect(inset, inset, w - inset * 2, d - inset * 2, 10, 'vp-sb__cushion'));
  } else {
    var cx0 = 0;
    var cx1 = w;
    out.push(rect(0, 0, w, bd, 8, 'vp-sb__back'));
    if (type === 'corner') {
      out.push(rect(w - bd, 0, bd, d, 8, 'vp-sb__back'));
      cx1 = w - bd;
    }
    if (type === 'armL') {
      out.push(rect(0, 0, aw, d, ar, 'vp-sb__arm'));
      cx0 = aw;
    }
    if (type === 'armR') {
      out.push(rect(w - aw, 0, aw, d, ar, 'vp-sb__arm'));
      cx1 = w - aw;
    }
    out.push(rect(cx0 + inset, bd + inset, cx1 - cx0 - inset * 2, d - bd - inset * 2, 10, 'vp-sb__cushion'));
  }
  if (sold) out.push(rect(0, 0, w, d, 8, 'vp-sb__sold', 'url(#' + this.soldId + ')'));
  return out.join('');
};

P.node = function (key, attrs) {
  var g = this.nodes[key];
  if (!g) {
    g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.innerHTML = '<g class="vp-sb__body"></g>';
    g.classList.add('is-entering');
    this.world.appendChild(g);
    this.nodes[key] = g;
    nextFrame(function () { g.classList.remove('is-entering'); });
  }
  Object.keys(attrs).forEach(function (k) { g.setAttribute(k, attrs[k]); });
  g.dataset.seen = '1';
  return g;
};

P.drawCanvas = function () {
  var self = this;
  var geo = this.geo;
  var sel = this.selection || {};
  this.slotList = this.slots();
  var f = this.fit(geo, this.slotList);
  this.world.style.transform = 'translate(' + f.tx.toFixed(1) + 'px,' + f.ty.toFixed(1) + 'px) scale(' + f.s.toFixed(4) + ')';
  Object.keys(this.nodes).forEach(function (k) { delete self.nodes[k].dataset.seen; });

  var bad = {};
  this.lineItems.forEach(function (l) { if (!l.ok) bad[l.role + (l.facing || '')] = true; });
  function sold(type) {
    var role = ROLE[type] || type;
    return !!(bad[role] || bad[role + (type === 'armL' ? 'left' : type === 'armR' ? 'right' : '')]);
  }
  // Create or update one interactive node; keyed nodes persist so moves animate.
  function put(key, cls, on, label, data, at, html) {
    var g = self.node(key, { class: cls + (on ? ' is-selected' : ''), role: 'button', tabindex: '0', 'aria-label': label, 'aria-pressed': on ? 'true' : 'false' });
    g.setAttribute(data[0], data[1]);
    g.style.transform = 'translate(' + at[0].toFixed(1) + 'px,' + at[1].toFixed(1) + 'px)' + (at[2] != null ? ' rotate(' + ANGLE[at[2]] + 'deg)' : '');
    g.firstChild.innerHTML = html;
  }

  var n = geo.items.length;
  geo.items.forEach(function (it, i) {
    var x = sold(it.type);
    put(it.id, 'vp-sb__piece' + (x ? ' is-sold' : ''), sel.kind === 'piece' && sel.id === it.id,
      self.labels[ROLE[it.type]] + ', ' + (i + 1) + ' of ' + n + (x ? ', not available in ' + self.colorLabel() : ''),
      ['data-vp-piece', it.id], [it.x, it.y, it.h], self.shape(it.type, it.w, it.d, x));
  });
  geo.ottomans.forEach(function (o) {
    var x = sold('ottoman');
    put(o.id, 'vp-sb__piece is-ottoman' + (x ? ' is-sold' : ''), sel.kind === 'ottoman' && sel.id === o.host,
      self.labels.ottoman + ', in front of piece ' + (geo.byId[o.host].index + 1),
      ['data-vp-ottoman', o.host], [o.x, o.y, o.h], self.shape('ottoman', o.w, o.d, x));
  });

  // + buttons keep a constant size on screen.
  var r = (this.slotPx * VB_W / (this.svg.clientWidth || VB_W)) / f.s;
  var k = (r * 0.42).toFixed(1);
  this.slotList.forEach(function (s) {
    put('slot-' + s.key, 'vp-sb__slot', sel.kind === 'slot' && sel.key === s.key,
      'Add a piece ' + (s.end ? 'at the ' + s.end : 'between ' + s.at + ' and ' + (s.at + 1)),
      ['data-vp-slot', s.key], self.slotPoint(s, r),
      '<circle class="vp-sb__slot-hit" r="' + (r * 1.35).toFixed(1) + '"></circle><circle class="vp-sb__slot-dot" r="' + r.toFixed(1) + '"></circle>' +
      '<path class="vp-sb__slot-plus" d="M-' + k + ' 0H' + k + 'M0 -' + k + 'V' + k + '" stroke-width="' + (r * 0.14).toFixed(2) + '"></path>');
  });

  // Fade out what is gone, then remove it.
  Object.keys(this.nodes).forEach(function (key) {
    var g = self.nodes[key];
    if (g.dataset.seen) return;
    delete self.nodes[key];
    ['tabindex', 'data-vp-piece', 'data-vp-ottoman', 'data-vp-slot'].forEach(function (a) { g.removeAttribute(a); });
    g.setAttribute('aria-hidden', 'true');
    g.classList.add('is-leaving');
    window.setTimeout(function () { if (g.parentNode) g.parentNode.removeChild(g); }, reduceMotion ? 0 : 180);
  });
  // Tab order follows the run: pieces, then ottomans, then + buttons.
  geo.items.concat(geo.ottomans).forEach(function (it) { self.world.appendChild(self.nodes[it.id]); });
  this.slotList.forEach(function (s) { self.world.appendChild(self.nodes['slot-' + s.key]); });

  var dims = $(this.root, '[data-vp-dims]');
  if (dims && this.inches && this.cfg.showDimensions) {
    var b = bounds(geo.items.map(function (i) { return i.rect; }));
    dims.textContent = Math.round((b.x1 - b.x0) / this.pxPerInch) + ' in wide \u00d7 ' + Math.round((b.y1 - b.y0) / this.pxPerInch) + ' in deep, without ottomans';
    dims.hidden = false;
  }
};

P.slotPoint = function (s, r) {
  var geo = this.geo;
  var items = geo.items;
  var gap = r * 1.5;
  if (s.end === 'start') {
    var a = items[0];
    return a.toWorld(-gap, a.d / 2);
  }
  if (s.end === 'end') {
    var z = items[items.length - 1];
    return z.toWorld(z.w + gap, z.d / 2);
  }
  // Between two pieces: just in front of the seam, so narrow pieces stay visible.
  var it = items[s.at];
  return it.toWorld(0, it.d + r * 1.2);
};

P.button = function (label, action, extra) {
  return '<button type="button" class="vp-sb__action' + (extra ? ' ' + extra : '') + '" data-vp-action="' + esc(action) + '">' + label + '</button>';
};

P.drawActions = function () {
  var box = $(this.root, '[data-vp-actions]');
  var sel = this.selection;
  var self = this;
  var html = '';
  if (sel && sel.kind === 'slot') {
    var slot = this.slotList.filter(function (s) { return s.key === sel.key; })[0];
    if (!slot) { this.selection = null; box.hidden = true; return; }
    var title = slot.end === 'start' ? 'Add at the start' : slot.end === 'end' ? 'Add at the end' : 'Add between ' + slot.at + ' and ' + (slot.at + 1);
    html += '<p class="vp-sb__actions-title">' + title + '</p><div class="vp-sb__actions-row">';
    this.insertOptions(slot).forEach(function (o) {
      var role = ROLE[o.type];
      var v = self.variantFor(role, o.type === 'armL' ? 'left' : o.type === 'armR' ? 'right' : null);
      html += self.button(esc(self.labels[role]) + (v ? ' <span class="vp-sb__action-price">+' + self.money.format(v.price / 100) + '</span>' : ''), 'insert:' + slot.key + ':' + o.type);
    });
    html += self.button('Cancel', 'clear', 'is-quiet') + '</div>';
  } else if (sel && sel.kind === 'piece') {
    var i = this.indexOf(sel.id);
    if (i < 0) { this.selection = null; box.hidden = true; return; }
    var p = this.state.run[i];
    var run = this.state.run;
    html += '<p class="vp-sb__actions-title">' + esc(this.labels[ROLE[p.type]]) + ' <span>' + (i + 1) + ' of ' + run.length + '</span></p><div class="vp-sb__actions-row">';
    if (SEATING[p.type] && this.cfg.pieces.ottoman) {
      if (this.state.ottomans[p.id]) {
        html += this.button('Remove ottoman', 'otto-off');
      } else if (Object.keys(this.state.ottomans).length < (this.cfg.maxOttomans || 0)) {
        var trial = Object.assign({}, this.state.ottomans);
        trial[p.id] = true;
        if (this.ottomansFit(run, trial)) html += this.button('Add ottoman in front', 'otto-on');
      }
    }
    var last = run.length - 1;
    if (p.type === 'armL' || p.type === 'armR') html += this.button('Take off armrest', 'arm-off');
    else if (p.type === 'seat' && i === 0 && this.validRun([this.piece('armL')].concat(run.slice(1)))) html += this.button('Add armrest', 'arm-on:L');
    else if (p.type === 'seat' && i === last && this.validRun(run.slice(0, last).concat([this.piece('armR')]))) html += this.button('Add armrest', 'arm-on:R');
    if (this.validRun(run.slice(0, i).concat(run.slice(i + 1)))) html += this.button('Remove', 'remove', 'is-danger');
    html += this.button('Done', 'clear', 'is-quiet') + '</div>';
  } else if (sel && sel.kind === 'ottoman') {
    html += '<p class="vp-sb__actions-title">' + esc(this.labels.ottoman) + '</p><div class="vp-sb__actions-row">' +
      this.button('Remove ottoman', 'otto-off') + this.button('Done', 'clear', 'is-quiet') + '</div>';
  }
  if (this.state.run.length >= (this.cfg.maxPieces || 12)) {
    html += '<p class="vp-sb__limit">That\u2019s the largest layout we offer online. A BoxDrop dealer can help with bigger rooms.</p>';
  }
  box.innerHTML = html;
  box.hidden = !html;
};

P.drawSummary = function () {
  var self = this;
  var lines = this.lineItems;
  var total = 0;
  var count = 0;
  var problems = [];
  var html = lines.map(function (l) {
    total += l.price;
    count += l.qty;
    if (!l.ok) problems.push(self.unavailableText(l));
    return '<li class="vp-sb__line-item' + (l.ok ? '' : ' is-sold') + '">' +
      '<span class="vp-sb__line-name">' + esc(l.label) + ' <span class="vp-sb__line-qty">\u00d7 ' + l.qty + '</span>' +
      (l.ok && l.left ? '<span class="vp-sb__line-left">Only ' + l.left + ' left in ' + esc(self.colorLabel()) + '</span>' : '') +
      (l.ok ? '' : '<span class="vp-sb__line-left">Not available</span>') + '</span>' +
      '<span class="vp-sb__line-price">' + (l.variant ? self.money.format(l.price / 100) : '\u2014') + '</span></li>';
  }).join('');
  $(this.root, '[data-vp-lines]').innerHTML = html;
  var totalText = this.money.format(total / 100);
  $(this.root, '[data-vp-total]').textContent = totalText;
  $(this.root, '[data-vp-sticky-total]').textContent = totalText;
  this.total = total;
  this.count = count;
  var ok = !problems.length && count > 0;
  var label = this.busy ? 'Adding\u2026' : 'Add ' + count + ' piece' + (count === 1 ? '' : 's') + ' to cart \u2014 ' + totalText;
  [this.addBtn, this.stickyBtn].forEach(function (b) {
    b.textContent = label;
    b.disabled = !ok || self.busy;
    b.classList.toggle('is-loading', self.busy);
  });
  if (problems.length) this.message(problems.join(' ') + ' Pick another fabric or remove it.', 'error');
  else if (this.lastMessageKind === 'error-layout') this.message('');
};

P.drawPresets = function () {
  var code = this.encode().split('~').slice(0, 2).join('~');
  var self = this;
  $$(this.root, '[data-vp-preset]').forEach(function (b) {
    var preset = self.presets[parseInt(b.getAttribute('data-vp-preset'), 10)];
    var match = false;
    if (preset) {
      var tokens = preset.run.map(function (p) { return TOKEN[p.type]; }).join('.');
      var idx = [];
      var left = preset.ottomans || 0;
      preset.run.forEach(function (p, i) { if (left > 0 && SEATING[p.type]) { idx.push(i); left -= 1; } });
      match = tokens + '~o' + idx.join('.') === code;
    }
    b.setAttribute('aria-pressed', match ? 'true' : 'false');
    b.hidden = !preset;
  });
};

P.drawArmNote = function () {
  var note = $(this.root, '[data-vp-arm-note]');
  if (!note) return;
  var hasArms = this.state.run.some(function (p) { return p.type === 'armL' || p.type === 'armR'; });
  var v = this.variantFor('arm', 'left');
  if (hasArms && (!v || !v.available)) {
    note.textContent = this.armLabel() + ' isn\u2019t available in ' + this.colorLabel() + ' right now.';
    note.hidden = false;
  } else {
    note.hidden = true;
  }
};

// Messages
P.message = function (text, kind) {
  var el = $(this.root, '[data-vp-message]');
  el.textContent = text || '';
  el.hidden = !text;
  el.className = 'vp-sb__message' + (kind ? ' is-' + kind : '');
  this.lastMessageKind = kind === 'error' ? 'error-layout' : kind;
};
P.say = function (text) {
  var live = $(this.root, '[data-vp-live]');
  live.textContent = '';
  window.setTimeout(function () { live.textContent = text; }, 30);
};
P.status = function (prefix) {
  return prefix + ' ' + this.count + ' piece' + (this.count === 1 ? '' : 's') + '. ' + this.money.format(this.total / 100) + '.';
};

// Events
P.bind = function () {
  var self = this;
  var root = this.root;

  $$(root, '[data-vp-preset]').forEach(function (b) {
    b.addEventListener('click', function () {
      var preset = self.presets[parseInt(b.getAttribute('data-vp-preset'), 10)];
      if (!preset) return;
      var st = self.fromPreset(preset);
      self.commit(st.run, st.ottomans, null);
      track('vp_builder_preset', { preset: preset.label, color: self.state.color });
      self.say(self.status(preset.label + '.'));
    });
  });
  $$(root, '[data-vp-color]').forEach(function (r) {
    r.addEventListener('change', function () {
      if (!r.checked) return;
      self.touch();
      self.state.color = r.value;
      self.dirty = true;
      root.classList.add('is-recolor');
      self.render(false);
      nextFrame(function () { root.classList.remove('is-recolor'); });
      self.say(self.status(self.colorLabel() + '.'));
    });
  });
  $$(root, '[data-vp-arm]').forEach(function (r) {
    r.addEventListener('change', function () {
      if (!r.checked) return;
      self.touch();
      self.state.arm = r.value;
      self.dirty = true;
      self.render(false);
      self.say(self.status(self.armLabel() + '.'));
    });
  });

  this.svg.addEventListener('click', function (e) { self.onCanvas(e.target); });
  this.svg.addEventListener('keydown', function (e) { self.onKey(e); });
  $(root, '[data-vp-actions]').addEventListener('click', function (e) {
    var b = e.target.closest('[data-vp-action]');
    if (b) self.act(b.getAttribute('data-vp-action'));
  });
  [this.addBtn, this.stickyBtn].forEach(function (b) {
    b.addEventListener('click', function () { self.addToCart(); });
  });
  $(root, '[data-vp-copy]').addEventListener('click', function () { self.copyLink(); });

  var resize;
  window.addEventListener('resize', function () {
    window.clearTimeout(resize);
    resize = window.setTimeout(function () { self.root.classList.add('is-instant'); self.drawCanvas(); self.root.classList.remove('is-instant'); }, 120);
  });
  this.bindSticky();
};

P.onCanvas = function (target) {
  var piece = target.closest('[data-vp-piece]');
  var otto = target.closest('[data-vp-ottoman]');
  var slot = target.closest('[data-vp-slot]');
  var sel = this.selection;
  if (piece) {
    var id = piece.getAttribute('data-vp-piece');
    this.selection = sel && sel.kind === 'piece' && sel.id === id ? null : { kind: 'piece', id: id };
  } else if (otto) {
    var host = otto.getAttribute('data-vp-ottoman');
    this.selection = sel && sel.kind === 'ottoman' && sel.id === host ? null : { kind: 'ottoman', id: host };
  } else if (slot) {
    var key = slot.getAttribute('data-vp-slot');
    var keep = sel && sel.kind === 'piece' && key.charAt(0) === 'b' ? sel : null;
    this.selection = sel && sel.kind === 'slot' && sel.key === key ? keep : { kind: 'slot', key: key, from: keep };
  } else {
    this.selection = null;
  }
  this.render(false);
  this.refocus();
};

P.refocus = function () {
  var sel = this.selection;
  var key = !sel ? null : sel.kind === 'piece' ? sel.id : sel.kind === 'ottoman' ? 'o' + sel.id : 'slot-' + sel.key;
  var node = key && this.nodes[key];
  if (node && document.activeElement !== node && this.root.contains(document.activeElement) && document.activeElement.closest('[data-vp-svg]')) {
    node.focus({ preventScroll: true });
  }
};

P.onKey = function (e) {
  var target = e.target.closest('[data-vp-piece],[data-vp-ottoman],[data-vp-slot]');
  if (!target) return;
  var key = e.key;
  if (key === 'Enter' || key === ' ') {
    e.preventDefault();
    this.onCanvas(target);
    var first = $(this.root, '[data-vp-actions] [data-vp-action]');
    if (first && this.selection) first.focus();
    return;
  }
  if (key === 'Escape') {
    this.selection = null;
    this.render(false);
    return;
  }
  var id = target.getAttribute('data-vp-piece');
  if (!id) return;
  var i = this.indexOf(id);
  if (key === 'ArrowRight' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowUp') {
    e.preventDefault();
    var j = key === 'ArrowRight' || key === 'ArrowDown' ? Math.min(i + 1, this.state.run.length - 1) : Math.max(i - 1, 0);
    var next = this.nodes[this.state.run[j].id];
    if (next) next.focus();
  } else if (key === 'Delete' || key === 'Backspace') {
    e.preventDefault();
    this.selection = { kind: 'piece', id: id };
    this.act('remove');
  }
};

P.act = function (action) {
  var sel = this.selection;
  var run = this.state.run;
  var parts = action.split(':');
  var ott = Object.assign({}, this.state.ottomans);
  if (parts[0] === 'clear') {
    this.selection = null;
    this.render(false);
    return;
  }
  if (parts[0] === 'insert') {
    var slot = this.slotList.filter(function (s) { return s.key === parts[1]; })[0];
    var opt = slot && this.insertOptions(slot).filter(function (o) { return o.type === parts[2]; })[0];
    if (!opt) return;
    this.commit(opt.run, ott, { kind: 'piece', id: opt.piece.id });
    this.say(this.status(this.labels[ROLE[opt.type]] + ' added.'));
    this.focusNode(opt.piece.id);
    return;
  }
  if (!sel) return;
  var id = sel.id;
  var i = this.indexOf(id);
  if (parts[0] === 'otto-on' && i > -1) {
    ott[id] = true;
    this.commit(run, ott, { kind: 'ottoman', id: id });
    this.say(this.status('Ottoman added.'));
  } else if (parts[0] === 'otto-off') {
    delete ott[id];
    this.commit(run, ott, i > -1 && sel.kind === 'piece' ? sel : null);
    this.say(this.status('Ottoman removed.'));
  } else if (parts[0] === 'arm-off' && i > -1) {
    var seat = this.piece('seat');
    seat.id = id;
    this.commit(run.slice(0, i).concat([seat], run.slice(i + 1)), ott, sel);
    this.say(this.status('Armrest removed.'));
  } else if (parts[0] === 'arm-on' && i > -1) {
    var arm = this.piece(parts[1] === 'L' ? 'armL' : 'armR');
    arm.id = id;
    this.commit(run.slice(0, i).concat([arm], run.slice(i + 1)), ott, sel);
    this.say(this.status('Armrest added.'));
  } else if (parts[0] === 'remove' && i > -1) {
    var next = run.slice(0, i).concat(run.slice(i + 1));
    if (!this.validRun(next)) return;
    var name = this.labels[ROLE[run[i].type]];
    delete ott[id];
    this.commit(next, ott, null);
    this.say(this.status(name + ' removed.'));
    var neighbour = next[Math.min(i, next.length - 1)];
    if (neighbour) this.focusNode(neighbour.id);
  }
};

P.focusNode = function (id) {
  var node = this.nodes[id];
  if (node) node.focus({ preventScroll: true });
};

// Cart
P.addToCart = function () {
  if (this.busy) return;
  var self = this;
  var lines = this.lineItems;
  if (!lines.length || lines.some(function (l) { return !l.ok; })) return;
  var corners = this.state.run.filter(function (p) { return p.type === 'corner'; }).length;
  var shape = corners === 2 ? 'U-shape' : corners === 1 ? 'L-shape' : Object.keys(this.state.ottomans).length ? 'Chaise' : 'Sofa';
  var code = this.encode();
  var props = { Layout: shape + ', ' + this.count + ' pieces, ' + this.colorLabel(), _layout: code };
  var items = lines.map(function (l) { return { id: l.variant.id, quantity: l.qty, properties: props }; });
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || this.cfg.cartRoot || '/';
  this.busy = true;
  this.message('');
  this.drawSummary();

  fetch(root + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: items })
  }).then(function (res) {
    return res.json().then(function (body) { return { ok: res.ok, status: res.status, body: body }; });
  }).then(function (r) {
    if (!r.ok) {
      var detail = (r.body && (r.body.description || r.body.message)) || '';
      throw { kind: 'stock', detail: detail };
    }
    var dealer = recall('vp-dealer');
    var after = dealer ? fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { dealer: dealer } })
    }).catch(function () { /* attribution is best effort */ }) : Promise.resolve();
    return after;
  }).then(function () {
    self.busy = false;
    self.drawSummary();
    self.message('Added to your cart.', 'success');
    self.say('Added ' + self.count + ' pieces to your cart.');
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish('vp_builder_add_to_cart', { layout: code, pieces: self.count, value: self.total / 100, currency: self.cfg.currency });
      }
    } catch (e) { /* analytics never blocks the sale */ }
    self.openCart(root);
  }).catch(function (err) {
    self.busy = false;
    self.drawSummary();
    if (err && err.kind === 'stock') {
      self.message((err.detail ? err.detail + ' ' : 'Part of this layout just sold out. ') + 'Pick another fabric or remove that piece.', 'error');
    } else {
      self.message('That didn\u2019t go through. Check your connection and try again.', 'error');
    }
  });
};

P.openCart = function (root) {
  var el = document.getElementById('cart-config');
  var theme = window.Shopify && window.Shopify.theme;
  if (!el || !theme || !theme.cart || !theme.ajaxCart) {
    window.location.href = root + 'cart';
    return;
  }
  var config = JSON.parse(el.textContent);
  theme.cart.getCart().then(function (cart) {
    theme.ajaxCart.updateView(config, cart);
    if (config.cart_action === 'drawer') theme.ajaxCart.showDrawer(config);
    else if (config.cart_action === 'modal_cart' || config.cart_action === 'mini_cart') theme.ajaxCart.showModal(config);
    else window.location.href = config.cart_url;
  }).catch(function () { window.location.href = root + 'cart'; });
};

P.copyLink = function () {
  var url = this.shareUrl();
  var self = this;
  track('vp_builder_share', { layout: this.encode() });
  function done() {
    self.message('Link copied. Anyone who opens it sees this layout.', 'success');
    self.say('Link copied.');
  }
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(url).then(done, function () { self.message('Copy this link: ' + url, 'info'); });
  } else {
    this.message('Copy this link: ' + url, 'info');
  }
};

// On small screens, keep the total and the button in reach while the builder is on screen.
P.bindSticky = function () {
  var bar = $(this.root, '[data-vp-sticky]');
  if (!bar || !('IntersectionObserver' in window)) return;
  var inSection = false;
  var buttonVisible = false;
  var mq = window.matchMedia('(max-width: 989px)');
  function update() { bar.hidden = !(mq.matches && inSection && !buttonVisible); }
  new IntersectionObserver(function (entries) {
    inSection = entries[0].isIntersecting;
    // The product page's own sticky bar would cover this one; step it aside.
    document.body.classList.toggle('vp-sb-in-view', inSection);
    update();
  }).observe($(this.root, '.vp-sb__grid'));
  new IntersectionObserver(function (entries) {
    buttonVisible = entries[0].isIntersecting;
    update();
  }).observe(this.addBtn);
  if (mq.addEventListener) mq.addEventListener('change', update);
};

function init(scope) {
  $$(scope || document, '[data-vp-builder]').forEach(function (el) {
    if (el.__vpBuilder) return;
    try {
      el.__vpBuilder = new Builder(el);
    } catch (e) {
      el.classList.add('is-broken');
      if (window.console) window.console.error('Sectional builder failed to start', e);
    }
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
else init();
document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
